/**
 * This is a custom entity manager that uses lokijs as a backend.
 * This provides a simple Entity-Component-System (ECS) API while
 * leveraging the powerful querying and serialization capabilities of lokijs.
 */
// @ts-ignore: lokijs doesn't have TypeScript types
import loki from 'lokijs';
import { componentRegistry } from './ComponentRegistry';
import {
  PlayerControlled,
  PathfindingAI,
  IsTerrain,
  ActorStats,
  HexPosition,
  TakesTurns,
  IntendsToAct,
  Faction,
  Archetype
} from '../components';
import type { Entity as IEntity } from '../types/Entity';
import type { Component } from '../types/Components';

/**
 * A wrapper around a lokijs document to provide a convenient API
 * for adding and removing components.
 */
class Entity implements IEntity {
  readonly id: number;
  readonly doc: any;
  readonly collection: any;

  constructor(doc: any, collection: any) {
    this.doc = doc;
    this.collection = collection;
    this.id = doc.$loki;
  }

  toString(): string {
    const parts = [`<Entity ${this.id}`];

    // Identify entity type
    const faction = this.getComponent(Faction);
    const archetype = this.getComponent(Archetype);

    if (archetype) parts.push(archetype.name);
    else if (faction) parts.push(faction.name);
    else if (this.hasComponent(IsTerrain)) {
      parts.push('Terrain');
      return parts.join(' ') + ']'; // Terrain has no other info
    }

    // Add actor-specific info
    if (this.hasComponent(ActorStats) && this.hasComponent(HexPosition) && this.hasComponent(TakesTurns)) {
      const pos = this.getComponent(HexPosition)!;
      const stats = this.getComponent(ActorStats)!;
      const turn = this.getComponent(TakesTurns)!;
      parts.push(
        `pos:(${pos.coords.key},${pos.bearing})`,
        `hp:${stats.health}`,
        `nrg:${turn.energy}`
      );
    }

    // Add intention info
    if (this.hasComponent(IntendsToAct)) {
      const { actionType, details } = this.getComponent(IntendsToAct)!;
      let detailString = '';
      if (details.coords) {
        detailString = `->${details.coords.key}`;
      } else if (details.bearing !== undefined) {
        detailString = `->${details.bearing}`;
      }
      parts.push(`intends:${actionType}${detailString}`);
    }

    return parts.join(' ') + '>';
  }

  /**
   * Derives a camelCase key from a component's class name.
   * e.g., HexPosition -> "hexPosition"
   */
  private _getComponentKey(ComponentClass: new (...args: any[]) => any): string {
    const componentName = ComponentClass.name;
    return componentName.charAt(0).toLowerCase() + componentName.slice(1);
  }

  addComponent<T extends Component>(
    ComponentClass: new (...args: any[]) => T,
    componentInstance?: T
  ): this {
    const key = this._getComponentKey(ComponentClass);
    this.doc[key] = (componentInstance === undefined) ? {} : componentInstance;
    this.collection.update(this.doc);
    return this;
  }

  getComponent<T extends Component>(
    ComponentClass: new (...args: any[]) => T
  ): T | undefined {
    const key = this._getComponentKey(ComponentClass);
    return this.doc[key];
  }

  hasComponent<T extends Component>(
    ComponentClass: new (...args: any[]) => T
  ): boolean {
    const key = this._getComponentKey(ComponentClass);
    return this.doc.hasOwnProperty(key) && typeof this.doc[key] === 'object';
  }

  removeComponent<T extends Component>(
    ComponentClass: new (...args: any[]) => T
  ): this {
    const key = this._getComponentKey(ComponentClass);
    delete this.doc[key];
    this.collection.update(this.doc);
    return this;
  }

  destroy(): void {
    this.collection.remove(this.doc);
  }

  getAllComponents(): Component[] {
    const components: Component[] = [];
    for (const key in this.doc) {
      if (key === 'meta' || key === '$loki') continue;
      // The value `true` is for tag components, which we can ignore here
      // as they won't have methods like getProposedOutcome.
      if (typeof this.doc[key] === 'object' && this.doc[key] !== null) {
        components.push(this.doc[key]);
      }
    }
    return components;
  }
}

/**
 * Manages all entities and their components using a lokijs database.
 */
class EntityManager {
  private db: loki;
  private entities: any; // LokiJS Collection

  constructor() {
    this.db = new loki('hexadroit.db');
    this.entities = this.db.addCollection('entities');
  }

  /**
   * Clears all entities from the manager. Useful for scripts.
   */
  clear(): void {
    this.db.removeCollection('entities');
    this.entities = this.db.addCollection('entities');
  }

  /**
   * Creates a new entity.
   */
  createEntity(): Entity {
    const doc = this.entities.insert({});
    return new Entity(doc, this.entities);
  }

  /**
   * Queries for entities that have all of the specified components.
   */
  query(
    componentClasses: (new (...args: any[]) => any)[]
  ): Entity[] {
    const query: Record<string, any> = {};
    componentClasses.forEach(ComponentClass => {
      const componentName = ComponentClass.name;
      const key = componentName.charAt(0).toLowerCase() + componentName.slice(1);
      query[key] = { '$exists': true };
    });

    const docs = this.entities.find(query);
    return docs.map((doc: any) => new Entity(doc, this.entities));
  }

  /**
   * Retrieves a single entity by its unique ID.
   */
  getEntity(id: number): Entity | null {
    const doc = this.entities.get(id);
    return doc ? new Entity(doc, this.entities) : null;
  }

  /**
   * Serializes the entire database state to a JSON string.
   */
  serialize(): string {
    return this.db.serialize();
  }

  /**
   * Loads the database state from a JSON string.
   */
  load(jsonString: string): void {
    this.db.loadJSON(jsonString);
    this.entities = this.db.getCollection('entities');
  }

  /**
   * Clears the database, loads from a JSON string, and rehydrates components.
   */
  resetAndLoad(jsonString: string): void {
    this.clear();
    this.load(jsonString);
    this.rehydrate();
  }

  /**
   * After loading from JSON, some component data may be plain objects
   * instead of class instances. This method "rehydrates" them into
   * their proper class instances so that their methods are available.
   */
  rehydrate(): void {
    if (!this.entities) {
      console.error('EntityManager: entities collection is null in rehydrate(). Did load() fail?');
      return;
    }
    const allDocs = this.entities.find();

    allDocs.forEach((doc: any) => {
      let wasUpdated = false;

      for (const key in doc) {
        if (key === 'meta' || key === '$loki') continue;

        const ComponentClass = componentRegistry.get(key);
        if (!ComponentClass) {
          // ignore; there are some metadata in here that are not components
          continue;
        }

        const componentData = doc[key];
        if (typeof componentData !== 'object' || componentData === null) continue;

        // Don't re-hydrate an already hydrated component
        if (componentData.constructor !== Object) continue;

        // Instantiate the component with the serialized data
        const newInstance = new ComponentClass(componentData);

        doc[key] = newInstance;
        wasUpdated = true;
      }

      if (wasUpdated) {
        try {
          this.entities.update(doc);
        } catch (error) {
          // In test environments with incomplete LokiJS metadata, update may fail
          // This is non-critical as it just updates change tracking
          console.warn('EntityManager: update failed during rehydrate (non-critical):', error);
        }
      }
    });
  }
}

// Export a singleton instance for the entire application to use.
const entityManager = new EntityManager();
export default entityManager;

// Export the EntityManager class for creating temporary instances
export { EntityManager };

// Re-export the Entity interface for external use (not the class)
export type { Entity } from '../types/Entity';
